import type { FC } from 'react';
import { memo, useCallback } from 'react';
import Image from '../../../../shared/components/Image';
import type { ITopWin } from '../../types/topWins.types';
import { useTimeAgo } from '../../hooks/useTimeAgo';
import TopWinAmount from '../TopWinAmount';
import ButtonPlay from '../../../games/components/GameCard/components/ButtonPlay/ButtonPlay';
import { useGameStartActions, usePendingGameKey } from '../../../games/hooks/useGameStart';
import { useGameSounds } from 'shared/hooks';
import { clickCounter } from 'core/services/EventTracker';
import { transactionTracker } from 'core/services/TransactionTracker';
import styles from './TopWinCard.module.css';

interface TopWinCardProps {
  win: ITopWin;
}

const TopWinCard: FC<TopWinCardProps> = ({ win }) => {
  const timeAgo = useTimeAgo(win.wonAt);
  const { startGame } = useGameStartActions();
  const { playSelectGameSound } = useGameSounds();
  const pendingGameKey = usePendingGameKey();
  const isLoading = pendingGameKey === win.gameKey;

  const handleClick = useCallback(() => {
    if (isLoading) return;
    clickCounter.incrementClick(`click_game_${win.gameKey}_multi_top_wins`);
    transactionTracker.trackGameOpen(win.gameName, win.gameKey, 'TOP_WIN_GAMES', 'multi');
    playSelectGameSound();
    startGame(win.gameKey);
  }, [isLoading, win.gameKey, win.gameName, startGame, playSelectGameSound]);

  return (
    <div className={styles.card} onClick={handleClick} data-loading={isLoading || undefined}>
      <div className={styles.thumbnail}>
        <Image src={`/verticals/${win.gameKey}`} alt={win.gameName} className={styles.thumbnailImg} />
      </div>
      <div className={styles.info}>
        <TopWinAmount win={win} />
        <div className={styles.meta}>
          <span className={styles.gameName}>{win.gameName}</span>
          <span className={styles.timeAgo}>{timeAgo}</span>
        </div>
      </div>
      <ButtonPlay onClick={handleClick} className={`egt_game_btn_play ${styles.playButton}`} />
    </div>
  );
};

export default memo(TopWinCard);
